export interface Transport {
  id: string;
  name: string;
  protocol: string;
  bind: string | null;
  local_net: string | null;
  external_media_address: string | null;
  external_signaling_address: string | null;
  external_signaling_port: number;
  tos: string | null;
  cos: number;
  allow_reload: boolean;
  allow_wildcard_certs: boolean;
  symmetric_transport: boolean;
  domain: string | null;
  cert_file: string | null;
  priv_key_file: string | null;
  ca_list_file: string | null;
  cipher: string | null;
  method: string | null;
  password: string | null;
  verify_client: string | null;
  verify_server: string | null;
  require_client_cert: boolean;
  tcp_keepalive_enable: boolean;
  tcp_keepalive_idle_time: number;
  tcp_keepalive_interval_time: number;
  tcp_keepalive_probe_count: number;
  websocket_write_timeout: number;
  created_at: string;
  updated_at: string;
}

export type TransportCreate = Omit<Transport, "id" | "created_at" | "updated_at">;
export type TransportUpdate = Partial<TransportCreate>;

export interface Auth {
  id: string;
  name: string;
  auth_type: string;
  username: string;
  password: string;
  password_digest: string | null;
  created_at: string;
  updated_at: string;
}

export type AuthCreate = Omit<Auth, "id" | "created_at" | "updated_at">;
export type AuthUpdate = Partial<AuthCreate>;

export interface Aor {
  id: string;
  name: string;
  max_contacts: number;
  minimum_expiration: number;
  maximum_expiration: number;
  qualify_frequency: number;
  qualify_timeout: number;
  qualify_2xx_only: boolean;
  authenticate_qualify: boolean;
  remove_existing: boolean;
  remove_unavailable: boolean;
  support_path: boolean;
  contact: string[];
  outbound_proxy: string | null;
  mailboxes: string | null;
  created_at: string;
  updated_at: string;
}

export type AorCreate = Omit<Aor, "id" | "created_at" | "updated_at">;
export type AorUpdate = Partial<AorCreate>;

export interface Endpoint {
  id: string;
  name: string;
  context_id: string;
  transport_id: string | null;
  disallow: string | null;
  allow: string | null;
  dtmf_mode: string;
  timers: string;
  timers_min_se: number;
  timers_sess_expires: number;
  direct_media: boolean;
  direct_media_method: string;
  force_rport: boolean;
  rewrite_contact: boolean;
  rtp_symmetric: boolean;
  rtp_ipv6: boolean;
  ice_support: boolean;
  media_address: string | null;
  media_encryption: string;
  callerid: string | null;
  callerid_privacy: string;
  from_user: string | null;
  from_domain: string | null;
  trust_id_inbound: boolean;
  trust_id_outbound: boolean;
  send_pai: boolean;
  send_rpid: boolean;
  language: string | null;
  moh_suggest: string;
  accountcode: string | null;
  mailboxes: string | null;
  acl: string | null;
  deny: string | null;
  permit: string | null;
  t38_udptl: boolean;
  t38_udptl_ec: string;
  fax_detect: boolean;
  auth_ids: string[];
  outbound_auth_ids: string[];
  aor_ids: string[];
  identify_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface EndpointCreate {
  name: string;
  context_id: string;
  transport_id: string;
  disallow: string | null;
  allow: string | null;
  dtmf_mode: string;
  timers: string;
  timers_min_se: number;
  timers_sess_expires: number;
  direct_media: boolean;
  direct_media_method: string;
  force_rport: boolean;
  rewrite_contact: boolean;
  rtp_symmetric: boolean;
  rtp_ipv6: boolean;
  ice_support: boolean;
  media_address: string | null;
  media_encryption: string;
  callerid: string | null;
  callerid_privacy: string;
  from_user: string | null;
  from_domain: string | null;
  trust_id_inbound: boolean;
  trust_id_outbound: boolean;
  send_pai: boolean;
  send_rpid: boolean;
  language: string | null;
  moh_suggest: string;
  accountcode: string | null;
  mailboxes: string | null;
  acl: string | null;
  deny: string | null;
  permit: string | null;
  t38_udptl: boolean;
  t38_udptl_ec: string;
  fax_detect: boolean;
  auth_ids: string[];
  outbound_auth_ids: string[];
  aor_ids: string[];
  identify_ids: string[];
}

export type EndpointUpdate = Partial<EndpointCreate>;

export interface Identify {
  id: string;
  name: string;
  endpoint_id: string | null;
  match: string | null;
  match_header: string | null;
  match_request_uri: string | null;
  srv_lookups: boolean;
  created_at: string;
  updated_at: string;
}

export type IdentifyCreate = Omit<Identify, "id" | "created_at" | "updated_at">;
export type IdentifyUpdate = Partial<IdentifyCreate>;

export interface DialplanContext {
  id: string;
  name: string;
  description: string | null;
  include_context: string | null;
  created_at: string;
  updated_at: string;
}

export type DialplanContextCreate = Omit<DialplanContext, "id" | "created_at" | "updated_at">;
export type DialplanContextUpdate = Partial<DialplanContextCreate>;

export interface AriApp {
  id: string;
  name: string;
  password: string;
  allowed_origins: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export type AriAppCreate = Omit<AriApp, "id" | "created_at" | "updated_at">;
export type AriAppUpdate = Partial<AriAppCreate>;

export interface WebsocketEndpoint {
  id: string;
  name: string;
  uri: string;
  protocols: string;
  connection_type: string;
  connection_timeout: number;
  reconnect_interval: number;
  reconnect_attempts: number;
  tls_enabled: boolean;
  ca_list_file: string | null;
  ca_list_path: string | null;
  cert_file: string | null;
  priv_key_file: string | null;
  verify_server_cert: boolean | null;
  verify_server_hostname: boolean | null;
  headers: Record<string, string> | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export type WebsocketEndpointCreate = Omit<WebsocketEndpoint, "id" | "created_at" | "updated_at">;
export type WebsocketEndpointUpdate = Partial<WebsocketEndpointCreate>;

export interface HepSettings {
  id: string;
  enabled: boolean;
  capture_address: string;
  capture_password: string;
  capture_id: number;
  uuid_type: string;
  created_at: string;
  updated_at: string;
}

export type HepSettingsUpdate = Partial<Omit<HepSettings, "id" | "created_at" | "updated_at">>;

export type ActionType =
  | "answer" | "hangup" | "dial" | "voicemail" | "set" | "goto"
  | "stasis" | "playback" | "noop" | "verbose" | "wait" | "queue"
  | "mixmonitor" | "return" | "bridge" | "echo" | "record" | "sipaddheader" | "senddtmf";

export interface ActionCreate {
  priority: number;
  type: ActionType;
  params: Record<string, unknown>;
}

export interface ActionResponse {
  id: string;
  priority: number;
  application: string;
  params: Record<string, unknown>;
  label: string | null;
}

export interface DialplanExtension {
  id: string;
  context_id: string;
  extension: string;
  actions: ActionResponse[];
  created_at: string;
  updated_at: string;
}

export interface DialplanExtensionCreate {
  context_id: string;
  extension: string;
  actions: ActionCreate[];
}

export type DialplanExtensionUpdate = Partial<{
  /** Moving an extension to another context. The form always sent this, but the
   *  backend schema did not declare it, so Pydantic dropped it and the move
   *  silently did nothing while reporting success. */
  context_id: string;
  extension: string;
  actions: ActionCreate[];
}>;

/** Mirrors ALL_MODULES in webapp/app/services/config_modules.py. `http` was
 *  listed here and in the config viewer but is not generated by this app — the
 *  backend rejects it, which the viewer rendered as a load failure. */
export type ModuleName = "all" | "pjsip" | "extensions" | "ari" | "websocket" | "hep";
export type ReloadModuleName = "all" | "pjsip" | "dialplan" | "ari" | "websocket";

export interface GenerateResult {
  status: string;
  results: Record<string, { path: string; sections: number }>;
}

export interface ReloadResult {
  status: string;
  results: Record<string, { success: boolean; message: string }>;
}

export const CODECS = ["ulaw", "alaw", "g722", "g729", "gsm", "opus", "h264", "vp8", "vp9"] as const;

// Codecs offered on a chan_websocket media stream (the c() option of Dial(WebSocket/...)).
// Includes slin* (raw signed-linear PCM) required by media/AI services like Pipecat, whose
// AsteriskFrameSerializer streams slin. slin = 8 kHz, slin16 = 16 kHz, slin24 = 24 kHz,
// slin48 = 48 kHz — match the AI pipeline's sample rate to avoid double-resampling
// (see docs/pipecat.md). Asterisk also has slin12/32/44/96/192 for other rates.
export const WEBSOCKET_CODECS = [
  "slin", "slin16", "slin24", "slin48",
  "ulaw", "alaw", "g722", "opus",
] as const;


export interface ActiveCall {
  channel: string;
  unique_id: string;
  linked_id: string | null;
  caller_id_num: string | null;
  caller_id_name: string | null;
  connected_line_num: string | null;
  connected_line_name: string | null;
  state: string | null;
  duration_seconds: number | null;
  jitter_rx: number | null;
  jitter_tx: number | null;
  packets_lost_rx: number | null;
  packets_lost_tx: number | null;
  rtt_seconds: number | null;
}

/**
 * One `call_log` row, i.e. one Asterisk *channel* — not one call. A single
 * outbound call bridged to a websocket writes four of these; `linked_id` ties
 * them together. `GET /api/calls` no longer returns these directly (see
 * `CallGroup`); they arrive nested under `CallGroup.channels`.
 */
export interface CallLog {
  id: string;
  unique_id: string | null;
  linked_id: string | null;
  /** SIP Call-ID of this channel's dialog. Null on Local and WebSocket legs,
   *  which have no SIP dialog. */
  sip_call_id: string | null;
  channel: string | null;
  caller_id_num: string | null;
  caller_id_name: string | null;
  connected_line_num: string | null;
  connected_line_name: string | null;
  direction: string | null;
  state: string | null;
  start_time: string | null;
  answer_time: string | null;
  end_time: string | null;
  duration_seconds: number | null;
  billable_seconds: number | null;
  hangup_cause: number | null;
  hangup_cause_txt: string | null;
  endpoint_name: string | null;
  context_name: string | null;
  dial_status: string | null;
  invite_headers: Record<string, string> | null;
  timestamp: string;
}

/**
 * One logical call, aggregated by the backend from the `call_log` rows of its
 * channels (`CallGroupResponse`). This is what `GET /api/calls` returns and
 * what `GET /api/calls/count` counts — grouping is `COUNT(DISTINCT linked_id)`,
 * so the numbers are calls, not the ~4x-inflated channel counts they used to be.
 *
 * The field names mirror `CallLog` so existing rendering keeps working, with two
 * differences: `id` is the `linked_id` **string**, not a `call_log` primary key,
 * and the raw per-leg rows hang off `channels`.
 */
export interface CallGroup extends CallLog {
  /** The call's `linked_id` (a string), not a `call_log` UUID. */
  id: string;
  /** Number of channels (legs) this call is made of. */
  /** Call-ID of the trunk leg — what a provider or a pcap shows for this call. */
  sip_call_id: string | null;
  /** Every distinct Call-ID across the legs. A call bridged between two trunks
   *  has one per dialog, so `sip_call_id` alone does not identify the call. */
  sip_call_ids: string[];
  channel_count: number;
  /** The raw per-leg `call_log` rows, earliest first. */
  channels: CallLog[];
}

export interface OriginateCallRequest {
  caller_type: "extension" | "endpoint";
  caller_ext?: string;
  caller_context?: string;
  caller_endpoint?: string;
  dest_type: "extension" | "trunk" | "sip_uri";
  dest_ext?: string;
  /** Context of dest_ext; defaults to caller_context when omitted. */
  dest_context?: string;
  dest_trunk?: string;
  dest_number?: string;
  dest_uri?: string;
  /** Display name only — sent as `"name" <number>`. */
  caller_name?: string;
  /** Number presented as caller ID; falls back to caller_ext. */
  caller_id_num?: string;
  timeout?: number;
}

export interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  record_name: string | null;
  operation: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  changed_fields: string[] | null;
  ip_address: string | null;
  user_agent: string | null;
  endpoint: string | null;
  timestamp: string;
}

export interface ServerStatus {
  public_ip: string | null;
  public_url: string | null;
}

export interface ConfigViewResponse {
  module: string;
  path: string;
  content: string;
}

export interface AmiCommandResponse {
  command: string;
  success: boolean;
  output: string;
}

/** "read" sees only the inspection tools on /mcp; "read_write" sees them all. */
export type ApiKeyRole = "read" | "read_write";

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  role: ApiKeyRole;
  revoked: boolean;
  created_at: string;
  last_used_at: string | null;
}

export interface ApiKeyCreate {
  name: string;
  role?: ApiKeyRole;
}

export interface ApiKeyCreated extends ApiKey {
  api_key: string;
}

export interface ConfigDriftModule {
  module: string;
  path: string;
  in_sync: boolean;
  reason: string | null;
}

export interface ConfigDrift {
  in_sync: boolean;
  drifted_modules: string[];
  modules: ConfigDriftModule[];
  /**
   * Modules whose config was written to disk but which Asterisk never applied,
   * because the AMI reload failed. Distinct from drift: disk and database agree,
   * so a byte-comparison alone would report these as in sync.
   */
  reload_pending?: string[];
}
